import { pushState } from '$app/navigation';
import { navigating, page } from '$app/state';
import { untrack } from 'svelte';

/**
 * This may seem overcomplicated with the active and enabled props, but all of this serves a purpose of preventing bugs, like all states closing on 1 back press, or having to press the back button multiple times to achieve what we want etc
 */
interface InterceptionOptions<T = void> {
    // Unique history state identifier (e.g. 'galleryOpen')
    stateKey: string;

    /**
     * Is the temporary state we want to block actively engaged right now? I.e. is the modal/overlay physically open right now, so that it can be closed on the back button press?
     * @default () => true
     *
     * @example
     * // CASE 1: OMIT this prop if your component is conditionally mounted (standard). For example, if we include this code in ImageLightbox, we don't have to specify the active prop, as it's always true, this interception should always run.
     *
     * @example
     * // CASE 2: PASS this prop if your component stays permanently mounted on the page, but displays its popup/drawer conditionally based on local state: For example, if we include this in WikiExplore, we only want to intercept back button clicks when the POI is active, NOT when WikiExplore simply exists and is mounted, so we do "active: () => activePoi !== null".
     * useBackButtonInterception({
     *   stateKey: 'popup',
     *   active: () => activePoi !== null,
     *   onBack,
     *   onForward: 'skip'
     * });
     */
    active?: () => boolean;

    /**
     * Environmental condition (e.g., only run on mobile).
     * @default () => true
     */
    enabled?: () => boolean;

    // Callback executed when browser/phone back button is pressed while the UI is open (close the UI)
    onBack: () => void;

    /**
     * Optional. Called when the UI opens, to capture data that gets stored in the history
     * entry (e.g. () => selectedCity). It is passed to onForward if the user returns to
     * this entry with the forward button. The result is snapshotted with $state.snapshot,
     * so it must be structured-cloneable (plain objects, arrays, primitives).
     */
    getData?: () => T;

    /**
     * What to do when the user goes FORWARD onto a history entry carrying this state
     * while the UI is closed. Required, so every call site states its intent:
     * - function: re-open the UI (restore). Receives the data captured by getData.
     * - 'skip': bounce back with history.back(). Use for transient UI that shouldn't
     *   reopen. Note: this makes any history entries after this one unreachable via forward.
     */
    onForward: ((data: T) => void) | 'skip';
}

export function useBackButtonInterception<T = void>(options: InterceptionOptions<T>) {
    const {
        stateKey,
        active = () => true,
        enabled = () => true,
        onBack,
        getData,
        onForward
    } = options;

    let hasPushed = false;
    let poppedByState = false;
    let pendingBack = false; // we called history.back() ourselves and are waiting for it to land
    let prevStateActive = untrack(() => !!(page.state as any)?.[stateKey]);

    const shouldIntercept = $derived<boolean>(active() && enabled());

    $effect(() => {
        const isActive = shouldIntercept;
        const isEnabled = enabled();
        const isStateActive = !!(page.state as any)?.[stateKey];
        // `navigating` is always an object; it's only mid-navigation when `.to` is set
        const isNavigating = navigating.to !== null;

        untrack(() => {
            const stateVanished = prevStateActive && !isStateActive;
            const stateAppeared = !prevStateActive && isStateActive;
            prevStateActive = isStateActive;

            console.debug(`[${stateKey}] effect run`, {
                isActive,
                isStateActive,
                isNavigating,
                hasPushed,
                pendingBack,
                poppedByState,
                stateVanished,
                stateAppeared
            });

            // 1. Real history transitions (back / forward)
            if (stateVanished) {
                if (pendingBack) {
                    console.debug(`[${stateKey}] Our own history.back() landed`);
                    pendingBack = false;
                } else if (isActive && hasPushed && !poppedByState) {
                    console.debug(`[${stateKey}] Back pressed, calling onBack`);
                    poppedByState = true;
                    onBack();
                }
            }

            if (stateAppeared && !isActive && !hasPushed && !pendingBack && isEnabled) {
                if (onForward === 'skip') {
                    console.debug(`[${stateKey}] Forward pressed, skipping`);
                    pendingBack = true;
                    history.back();
                    return;
                }

                // Data that was stored in the history entry we just landed on
                const data = (page.state as any)[stateKey]?.data as T;
                console.debug(`[${stateKey}] Forward pressed, restoring UI`, data);

                // mark as pushed BEFORE opening so step 2 doesn't push a second entry
                hasPushed = true;
                poppedByState = false;
                onForward(data);
                return;
            }

            // 2. UI open/close changes
            if (isActive && !hasPushed) {
                if (isStateActive) {
                    // Entry with our key already exists (stale); don't stack another one
                    console.debug(`[${stateKey}] State already present, not pushing`);
                } else {
                    console.debug(`[${stateKey}] Pushing state`);
                    pushState('', {
                        ...page.state,
                        [stateKey]: { data: getData ? $state.snapshot(getData()) : undefined }
                    });
                }
                hasPushed = true;
                poppedByState = false;
            } else if (!isActive && hasPushed) {
                if (!poppedByState && isStateActive && !isNavigating) {
                    console.debug(`[${stateKey}] UI closed manually, calling history.back()`);
                    pendingBack = true;
                    history.back();
                } else {
                    console.debug(`[${stateKey}] UI closed, history.back() NOT called`, {
                        poppedByState,
                        isStateActive,
                        isNavigating
                    });
                }
                hasPushed = false;
            }
        });
    });
}